import type { ButtonProps } from '@mui/material/Button';
import type { CSSObject } from '@mui/material/styles';

import { componentTokens, transitions } from '../../KestrixDesignSystem.jsx';

type ButtonSize = ButtonProps["size"];

export type ButtonColors = {
  background: string;
  color: string;
  border: string;
  hover?: string;
  hoverBorder?: string;
  hoverColor?: string;
  pressed?: string;
  pressedBorder?: string;
  pressedColor?: string;
  focusRing?: string;
  shadow: string;
  disabled: {
    background: string;
    color: string;
    border: string;
  };
};

export const getButtonSizeTokens = (size: ButtonSize = "medium", tokens = componentTokens.button) => {
  const sizeMap = {
    small: "xs",
    medium: "sm",
    large: "sm",
  } as const;

  const key = sizeMap[size ?? "medium"] || "sm";
  return {
    height: tokens.height[key],
    padding: tokens.padding[key],
    fontSize: tokens.fontSize[key],
  };
};

export const baseButtonStyles = (buttonColors: ButtonColors, size: ButtonSize = "medium"): CSSObject => {
  const tokens = componentTokens.button;
  const sizeTokens = getButtonSizeTokens(size, tokens);

  const styles: CSSObject = {
    background: buttonColors.background,
    color: buttonColors.color,
    border: `1px solid ${buttonColors.border}`,
    borderRadius: tokens.borderRadius,
    padding: sizeTokens.padding,
    fontWeight: tokens.fontWeight,
    fontSize: sizeTokens.fontSize,
    lineHeight: "20px",
    cursor: "pointer",
    transition: transitions.base,
    textTransform: "none",
    whiteSpace: "nowrap",
    overflow: "visible",
    position: "relative",
    minWidth: "auto",
    minHeight: sizeTokens.height,
    boxShadow: buttonColors.shadow,
    "&:hover": {
      background: buttonColors.hover || buttonColors.background,
      borderColor: buttonColors.hoverBorder || buttonColors.border,
      color: buttonColors.hoverColor || buttonColors.color,
      boxShadow: buttonColors.shadow,
    },
    "&:active": {
      background: buttonColors.pressed || buttonColors.hover || buttonColors.background,
      borderColor: buttonColors.pressedBorder || buttonColors.hoverBorder || buttonColors.border,
      color: buttonColors.pressedColor || buttonColors.color,
      boxShadow: buttonColors.shadow,
      transform: "none",
    },
    "&.Mui-focusVisible": {
      outline: buttonColors.focusRing ? `2px solid ${buttonColors.focusRing}` : "none",
      outlineOffset: buttonColors.focusRing ? "2px" : undefined,
    },
    "&:disabled": {
      background: buttonColors.disabled.background,
      color: buttonColors.disabled.color,
      cursor: "not-allowed !important",
      border: `1px solid ${buttonColors.disabled.border}`,
      boxShadow: "none",
      pointerEvents: "auto",
    },
  };

  return styles;
};
